import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/Header';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { IconPicker } from '../components/IconPicker/IconPicker';
import { useAppStore } from '../store/useAppStore';
import { apiClient } from '../api/client';
import { Category } from '../types';
import { getCategoryIconConfig, getCategoryIcon, getCategoryColor, getCategoryBackgroundColor } from '../utils/categoryIcons';
import { CustomIconOption, getCustomIconByName } from '../utils/customCategoryIcons';
import { saveCategoryIcon, getAllCategoryIcons, deleteCategoryIcon, categoryIconsEventEmitter, CATEGORY_ICONS_CHANGED_EVENT } from '../utils/categoryIconStorage';
import { useWebPowerSyncCategories } from '../hooks/usePowerSync';
import './CategoriesScreen.css';

interface CategoriesContentProps {
  showHeader?: boolean;
}

export const CategoriesContent: React.FC<CategoriesContentProps> = ({ showHeader = false }) => {
  const { t } = useTranslation();
  const { categories: storeCategories, setCategories, isLoading, setLoading } = useAppStore();
  
  // PowerSync данные
  const { categories: psCategories } = useWebPowerSyncCategories();
  const categories = psCategories.length > 0 ? psCategories.map((c: any) => ({
    id: c.name || c.id,
    name: c.name,
    emoji: c.icon || c.emoji || null,
    is_system: c.is_system,
    type: 'both' as const,
  })) : storeCategories;
  const [refreshing, setRefreshing] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [selectedIcon, setSelectedIcon] = useState<CustomIconOption | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editedCategoryName, setEditedCategoryName] = useState('');
  const [editingSelectedIcon, setEditingSelectedIcon] = useState<CustomIconOption | null>(null);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [categoryIcons, setCategoryIcons] = useState<Record<string, string>>({});

  useEffect(() => {
    loadCategories();
    loadCategoryIcons();
    
    // Подписываемся на изменения иконок
    const handleIconsChanged = (newIcons: Record<string, string>) => {
      setCategoryIcons(newIcons);
    };
    
    categoryIconsEventEmitter.on(CATEGORY_ICONS_CHANGED_EVENT, handleIconsChanged);
    
    return () => {
      categoryIconsEventEmitter.off(CATEGORY_ICONS_CHANGED_EVENT, handleIconsChanged);
    };
  }, []);

  const loadCategoryIcons = () => {
    const icons = getAllCategoryIcons();
    setCategoryIcons(icons);
  };

  const loadCategories = useCallback(async () => {
    if (psCategories.length > 0) {
      console.log('🏷️ [PowerSync] Using PowerSync categories, skipping API call');
      return;
    }
    try {
      setLoading(true);
      const allCategoriesData = await apiClient.getAllCategories();
      
      // Преобразуем системные категории
      const systemCategories = (allCategoriesData.system_categories || [])
        .filter((cat: any) => cat && cat.db_name && typeof cat.db_name === 'string')
        .map((cat: any) => ({
          id: cat.db_name,
          name: cat.db_name,
          emoji: cat.emoji || null,
          is_system: true,
          type: 'both' as const,
        }));
      
      // Преобразуем пользовательские категории
      const storedIcons = getAllCategoryIcons();
      const userCategories = (allCategoriesData.user_categories || [])
        .filter((catName: any) => catName && typeof catName === 'string')
        .map((catName: string) => ({
          id: catName,
          name: catName,
          emoji: storedIcons[catName] || null,
          is_system: false,
          type: 'both' as const,
        }));
      
      // Объединяем все категории
      const allCategories: Category[] = [
        ...systemCategories.sort((a: Category, b: Category) => a.name.localeCompare(b.name)),
        ...userCategories.sort((a: Category, b: Category) => a.name.localeCompare(b.name)),
      ];
      
      setCategories(allCategories);
    } catch (error) {
      console.error('❌ Error loading categories:', error);
      alert(t('categoriesScreen.loadError'));
    } finally {
      setLoading(false);
    }
  }, [setCategories, setLoading]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadCategories();
    loadCategoryIcons();
    setRefreshing(false);
  }, [loadCategories]);

  const handleOpenAddCategoryModal = () => {
    setNewCategory('');
    setSelectedIcon(null);
    setShowAddCategoryModal(true);
  };

  const handleCloseAddCategoryModal = () => {
    setShowAddCategoryModal(false);
    setNewCategory('');
    setSelectedIcon(null);
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      alert(t('categoriesScreen.enterName'));
      return;
    }

    try {
      setIsAddingCategory(true);
      const response = await apiClient.addCategory(newCategory.trim());
      
      // Сохраняем иконку локально, если она была выбрана
      if (selectedIcon) {
        await saveCategoryIcon(newCategory.trim(), selectedIcon.icon);
      }
      
      setNewCategory('');
      setSelectedIcon(null);
      setShowAddCategoryModal(false);
      await loadCategories();
      loadCategoryIcons();
    } catch (error: any) {
      console.error('❌ Error adding category:', error);
      alert(error.message || t('categoriesScreen.addError'));
    } finally {
      setIsAddingCategory(false);
    }
  };

  const handleStartEditCategory = (category: Category) => {
    setEditingCategory(category);
    setEditedCategoryName(category.name || '');
    
    // Загружаем текущую иконку категории
    if (category.emoji) {
      const customIcon = getCustomIconByName(category.emoji);
      if (customIcon) {
        setEditingSelectedIcon(customIcon);
      } else {
        setEditingSelectedIcon(null);
      }
    } else {
      setEditingSelectedIcon(null);
    }
    
    setShowEditCategoryModal(true);
  };

  const handleCancelEditCategory = () => {
    setEditingCategory(null);
    setEditedCategoryName('');
    setEditingSelectedIcon(null);
    setShowEditCategoryModal(false);
  };

  const handleSaveEditCategory = async () => {
    if (!editingCategory || !editedCategoryName.trim()) {
      handleCancelEditCategory();
      return;
    }

    const nameChanged = editedCategoryName.trim() !== editingCategory.name;
    const iconChanged = editingSelectedIcon?.icon !== editingCategory.emoji;

    // Если ничего не изменилось, просто закрываем
    if (!nameChanged && !iconChanged) {
      handleCancelEditCategory();
      return;
    }

    try {
      setIsEditingCategory(true);
      
      // Если изменилось название, переименовываем категорию
      if (nameChanged) {
        await apiClient.renameCategory(editingCategory.name, editedCategoryName.trim());
        
        // Если категория была переименована, нужно обновить иконку для нового имени
        if (editingSelectedIcon) {
          // Удаляем старую иконку
          await deleteCategoryIcon(editingCategory.name);
          // Сохраняем новую иконку для нового имени
          await saveCategoryIcon(editedCategoryName.trim(), editingSelectedIcon.icon);
        } else {
          // Удаляем старую иконку, если она была
          await deleteCategoryIcon(editingCategory.name);
        }
      } else if (iconChanged) {
        // Если изменилась только иконка
        if (editingSelectedIcon) {
          await saveCategoryIcon(editingCategory.name, editingSelectedIcon.icon);
        } else {
          await deleteCategoryIcon(editingCategory.name);
        }
      }
      
      handleCancelEditCategory();
      await loadCategories();
      loadCategoryIcons();
    } catch (error: any) {
      console.error('❌ Error updating category:', error);
      alert(error.message || t('categoriesScreen.updateError'));
    } finally {
      setIsEditingCategory(false);
    }
  };

  const handleDeleteCategory = async (name: string) => {
    if (!confirm(t('categoriesScreen.deleteConfirm', { name }))) {
      return;
    }

    try {
      await apiClient.deleteCategory(name);
      // Удаляем сохраненную иконку
      await deleteCategoryIcon(name);
      await loadCategories();
      loadCategoryIcons();
    } catch (error: any) {
      console.error('❌ Error deleting category:', error);
      alert(error.message || t('categoriesScreen.deleteError'));
    }
  };

  const safeCategories = Array.isArray(categories) ? categories : [];
  
  const systemCategories = safeCategories.filter((cat): cat is Category => {
    return cat !== null && 
      cat !== undefined && 
      typeof cat === 'object' && 
      (cat.is_system === true || cat.is_system === 'true' || String(cat.is_system) === 'true');
  });
  
  const userCategories = safeCategories.filter((cat): cat is Category => {
    return cat !== null && 
      cat !== undefined && 
      typeof cat === 'object' && 
      (cat.is_system === false || cat.is_system === 'false' || (cat.is_system !== true && cat.is_system !== 'true'));
  });

  if (isLoading && !refreshing) {
    return (
      <div className="categories-screen">
        {showHeader && <Header title="Категории" />}
        <div className="categories-screen__loading">
          <Icon name="loader" size={32} />
          <p>Загрузка категорий...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="categories-screen">
      {showHeader && <Header title="Категории" />}
      
      <div className="categories-screen__content">
        {/* Карточка добавления категории */}
        <Card className="categories-screen__add-card" onClick={handleOpenAddCategoryModal}>
          <div className="categories-screen__add-content">
            <Icon name="add" size={24} color="#AF52DE" />
            <span className="categories-screen__add-text">Добавить категорию</span>
          </div>
        </Card>

        {/* Пользовательские категории */}
        {userCategories.length > 0 && (
          <div className="categories-screen__section">
            <div className="categories-screen__section-header">
              <h2 className="categories-screen__section-title">Ваши категории</h2>
              <span className="categories-screen__section-count">{userCategories.length}</span>
            </div>
            
            <div className="categories-screen__categories-list">
              {userCategories.map((category) => {
                const customIconName = categoryIcons[category.name] || category.emoji || null;
                const iconConfig = getCategoryIconConfig(category.name, customIconName);
                
                return (
                  <Card key={category.id} className="categories-screen__category-card">
                    <div className="categories-screen__category-content">
                      <div
                        className="categories-screen__category-icon"
                        style={{ backgroundColor: iconConfig.backgroundColor }}
                      >
                        <Icon
                          name={iconConfig.icon}
                          size={20}
                          color={iconConfig.color}
                        />
                      </div>
                      <span className="categories-screen__category-name">{category.name}</span>
                    </div>
                    <div className="categories-screen__category-actions">
                      <button
                        className="categories-screen__category-action"
                        onClick={() => handleStartEditCategory(category)}
                        type="button"
                      >
                        <Icon name="pencil" size={18} color="#AF52DE" />
                      </button>
                      <button
                        className="categories-screen__category-action"
                        onClick={() => handleDeleteCategory(category.name)}
                        type="button"
                      >
                        <Icon name="delete" size={18} color="#FF3B30" />
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Системные категории */}
        <div className="categories-screen__section">
          <div className="categories-screen__section-header">
            <h2 className="categories-screen__section-title">Системные категории</h2>
            <span className="categories-screen__section-count">{systemCategories.length}</span>
          </div>
          <p className="categories-screen__section-subtitle">
            Эти категории нельзя изменить или удалить
          </p>

          {systemCategories.length > 0 ? (
            <div className="categories-screen__system-grid">
              {systemCategories.map((category) => {
                const iconConfig = getCategoryIconConfig(category.name);
                
                return (
                  <Card key={category.id} className="categories-screen__system-card">
                    <div className="categories-screen__system-content">
                      <div
                        className="categories-screen__category-icon"
                        style={{ backgroundColor: iconConfig.backgroundColor }}
                      >
                        <Icon
                          name={iconConfig.icon}
                          size={24}
                          color={iconConfig.color}
                        />
                      </div>
                      <span className="categories-screen__system-name">{category.name}</span>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="categories-screen__empty-card">
              <Icon name="folder" size={48} color="var(--color-text-secondary)" />
              <p className="categories-screen__empty-text">Нет системных категорий</p>
            </Card>
          )}
        </div>

        {/* Пустое состояние для пользовательских категорий */}
        {userCategories.length === 0 && (
          <div className="categories-screen__section">
            <div className="categories-screen__section-header">
              <h2 className="categories-screen__section-title">Ваши категории</h2>
            </div>
            <Card className="categories-screen__empty-card">
              <Icon name="folder" size={48} color="var(--color-text-secondary)" />
              <p className="categories-screen__empty-text">У вас пока нет своих категорий</p>
              <p className="categories-screen__empty-hint">
                Добавьте категорию выше, чтобы лучше организовать свои транзакции
              </p>
            </Card>
          </div>
        )}
      </div>

      {/* Модальное окно добавления категории */}
      <Modal
        isOpen={showAddCategoryModal}
        onClose={handleCloseAddCategoryModal}
        title="Новая категория"
        size="80vh"
      >
        <div className="categories-screen__modal-content">
          {/* Выбор иконки */}
          <IconPicker
            selectedIcon={selectedIcon?.icon}
            onIconSelect={setSelectedIcon}
          />

          {/* Название категории */}
          <div className="categories-screen__modal-input-container">
            <label className="categories-screen__modal-label">Название категории</label>
            <input
              type="text"
              className="categories-screen__modal-input neo-inset"
              placeholder="Введите название..."
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              disabled={isAddingCategory}
            />
          </div>

          {/* Кнопки */}
          <div className="categories-screen__modal-buttons">
            <Button
              variant="secondary"
              onClick={handleCloseAddCategoryModal}
              disabled={isAddingCategory}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleAddCategory}
              disabled={!newCategory.trim() || isAddingCategory}
              loading={isAddingCategory}
            >
              Создать
            </Button>
          </div>
        </div>
      </Modal>

      {/* Модальное окно редактирования категории */}
      <Modal
        isOpen={showEditCategoryModal}
        onClose={handleCancelEditCategory}
        title="Редактировать категорию"
        size="80vh"
      >
        <div className="categories-screen__modal-content">
          {/* Выбор иконки */}
          <IconPicker
            selectedIcon={editingSelectedIcon?.icon}
            onIconSelect={setEditingSelectedIcon}
          />

          {/* Название категории */}
          <div className="categories-screen__modal-input-container">
            <label className="categories-screen__modal-label">Название категории</label>
            <input
              type="text"
              className="categories-screen__modal-input neo-inset"
              placeholder="Введите название..."
              value={editedCategoryName}
              onChange={(e) => setEditedCategoryName(e.target.value)}
              disabled={isEditingCategory}
            />
          </div>

          {/* Кнопки */}
          <div className="categories-screen__modal-buttons">
            <Button
              variant="secondary"
              onClick={handleCancelEditCategory}
              disabled={isEditingCategory}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveEditCategory}
              disabled={!editedCategoryName.trim() || isEditingCategory}
              loading={isEditingCategory}
            >
              Сохранить
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export const CategoriesScreen: React.FC = () => {
  return <CategoriesContent showHeader={true} />;
};

